import { useState, useRef, useEffect } from "react";
import { useToast } from "@/hooks/use-toast";
import {
  useListProducts, useListCategories, useCreateProduct, useUpdateProduct, useDeleteProduct,
  getListProductsQueryKey, type ProductImportBarcodePreview,
} from "@workspace/api-client-react";
import { useQueryClient } from "@tanstack/react-query";
import { Link } from "wouter";
import { useAuth } from "@/context/auth";
import { Button } from "@/components/ui/button";
import { ToastAction } from "@/components/ui/toast";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, Plus, Edit, Trash2, Search, AlertTriangle, Upload, FileSpreadsheet, Crown, CheckCircle, XCircle, X, Download, Package, Printer, SlidersHorizontal, TrendingUp, TrendingDown, Minus, Info, ChevronDown, ChevronUp, ChevronLeft, ChevronRight, Layers, Check, ChevronsUpDown } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { formatCurrency } from "@/lib/currency";
import { format } from "date-fns";
import * as XLSX from "xlsx";
import { ProductForm, PRODUCT_FORM_DEFAULTS, type ProductFormValues } from "@/components/product-form";
import {
  createManualProductEntryRow,
  ManualProductEntryGrid,
  type ManualProductEntryRow,
} from "@/components/manual-product-entry-grid";
import { VariantMatrix } from "@/components/variant-matrix";
import { BarcodePrinter } from "@/components/barcode-printer";
import { parseLabelVariants, printableProducts, printableVariant } from "@/lib/variant-labels";
import { useBranch } from "@/context/branch";
import { trackEvent } from "@/lib/analytics";
import { matchesProductSearch, sortProductsBySearch } from "@/lib/product-search";

const BASE = import.meta.env.BASE_URL.replace(/\/$/, "");

function productSaveError(error: unknown): string {
  return error instanceof Error
    ? error.message.replace(/^HTTP \d+ [^:]*:\s*/, "")
    : "Please check the product details and try again.";
}

function isPlanUpgradeError(error: unknown): boolean {
  const data = (error as { data?: unknown } | null)?.data;
  return !!data && typeof data === "object" && (data as { upgrade?: unknown }).upgrade === true;
}

function getPageNumbers(current: number, total: number): (number | "...")[] {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  if (current <= 4) return [1, 2, 3, 4, 5, "...", total];
  if (current >= total - 3) return [1, "...", total - 4, total - 3, total - 2, total - 1, total];
  return [1, "...", current - 1, current, current + 1, "...", total];
}

// ----- Stock Adjustment Dialog -----
const ADJUST_REASONS = [
  { value: "physical_count", label: "Physical Count / Stock Take" },
  { value: "received_goods", label: "Received Goods (without purchase order)" },
  { value: "damaged", label: "Damaged / Expired Goods" },
  { value: "theft_loss", label: "Theft / Loss / Shrinkage" },
  { value: "return_supplier", label: "Returned to Supplier" },
  { value: "data_correction", label: "Data Correction" },
  { value: "other", label: "Other" },
];

interface AdjustProduct {
  id: number;
  name: string;
  sku: string;
  stock: number;
  unit: string;
  hasVariants?: boolean;
  variants?: string | null;
}

type AdjustVariant = {
  id: string;
  label: string;
  sku: string;
  price: number;
  stock: number;
  attribute1Label?: string;
  attribute1Value?: string;
  attribute2Label?: string;
  attribute2Value?: string;
};

function parseAdjustmentVariants(raw?: string | null): AdjustVariant[] | null {
  if (!raw) return [];
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return null;
    return parsed.map(value => {
      if (!value || typeof value !== "object" || Array.isArray(value)) throw new Error("Invalid variant");
      const row = value as Record<string, unknown>;
      const id = typeof row.id === "string" ? row.id.trim() : "";
      const label = typeof row.label === "string" ? row.label.trim() : "";
      const sku = typeof row.sku === "string" ? row.sku : "";
      const priceValue = row.price;
      const stockValue = row.stock;
      const price = typeof priceValue === "number" || (typeof priceValue === "string" && priceValue.trim() !== "")
        ? Number(priceValue)
        : Number.NaN;
      const stock = typeof stockValue === "number" || (typeof stockValue === "string" && stockValue.trim() !== "")
        ? Number(stockValue)
        : Number.NaN;
      if (!id || !label || !Number.isFinite(price) || price < 0 || !Number.isFinite(stock) || stock < 0) {
        throw new Error("Invalid variant");
      }
      return {
        id,
        label,
        sku,
        price,
        stock,
        ...(typeof row.attribute1Label === "string" ? { attribute1Label: row.attribute1Label } : {}),
        ...(typeof row.attribute1Value === "string" ? { attribute1Value: row.attribute1Value } : {}),
        ...(typeof row.attribute2Label === "string" ? { attribute2Label: row.attribute2Label } : {}),
        ...(typeof row.attribute2Value === "string" ? { attribute2Value: row.attribute2Value } : {}),
      };
    });
  } catch {
    return null;
  }
}

function StockAdjustDialog({ product, onClose }: { product: AdjustProduct; onClose: () => void }) {
  const queryClient = useQueryClient();
  const updateProduct = useUpdateProduct();
  const { toast } = useToast();
  const variants = product.hasVariants ? parseAdjustmentVariants(product.variants) : null;
  const [selectedVariantId, setSelectedVariantId] = useState(variants?.[0]?.id ?? "");
  const selectedVariant = variants?.find(variant => variant.id === selectedVariantId);
  const [physicalCount, setPhysicalCount] = useState<string>(() =>
    product.hasVariants ? String(variants?.[0]?.stock ?? "") : String(product.stock)
  );
  const [reason, setReason] = useState("physical_count");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);

  const physical = Number(physicalCount);
  const currentStock = product.hasVariants ? selectedVariant?.stock ?? 0 : product.stock;
  const isValid = physicalCount.trim() !== "" && Number.isInteger(physical) && physical >= 0 &&
    (!product.hasVariants || (variants !== null && variants.length > 0 && !!selectedVariant));
  const diff = isValid ? physical - currentStock : 0;

  const handleSave = async () => {
    if (!isValid) return;
    setSaving(true);
    try {
      const data = product.hasVariants
        ? {
            hasVariants: true,
            variants: JSON.stringify(variants!.map(variant => ({
              ...variant,
              stock: variant.id === selectedVariantId ? physical : variant.stock,
            }))),
            adjustmentReason: reason,
            adjustmentNotes: notes,
          }
        : {
            stock: physical,
            adjustmentReason: reason,
            adjustmentNotes: notes,
          };
      await updateProduct.mutateAsync({
        id: product.id,
        data: data as any,
      });
      setDone(true);
      void queryClient.invalidateQueries({ queryKey: getListProductsQueryKey() });
      setTimeout(onClose, 1200);
    } catch (error) {
      toast({
        title: "Stock adjustment not saved",
        description: error instanceof Error ? error.message : "Please check the value and try again.",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-5">
      {/* Product info */}
      <div className="bg-muted/40 rounded-lg px-4 py-3 flex items-center justify-between">
        <div>
          <p className="font-semibold">{product.name}</p>
          <p className="text-xs text-muted-foreground">SKU: {product.sku}</p>
        </div>
        <div className="text-right">
          <p className="text-xs text-muted-foreground">{product.hasVariants ? "Product total" : "System stock"}</p>
          <p className="text-2xl font-bold">{product.stock} <span className="text-sm font-normal text-muted-foreground">{product.unit}</span></p>
        </div>
      </div>

      {product.hasVariants && (
        variants && variants.length > 0 ? (
          <div className="space-y-1.5">
            <label className="text-sm font-medium">Size / color variant</label>
            <Select
              value={selectedVariantId}
              onValueChange={value => {
                setSelectedVariantId(value);
                const nextVariant = variants.find(variant => variant.id === value);
                if (nextVariant) setPhysicalCount(String(nextVariant.stock));
              }}
            >
              <SelectTrigger><SelectValue placeholder="Choose a variant" /></SelectTrigger>
              <SelectContent>
                {variants.map(variant => (
                  <SelectItem key={variant.id} value={variant.id}>
                    {variant.label}{variant.sku ? ` · ${variant.sku}` : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              Adjust one variant at a time. The product total changes by the same difference; any existing count discrepancy is preserved.
            </p>
          </div>
        ) : (
          <div className="flex items-start gap-2 rounded-lg border border-destructive/40 bg-destructive/5 px-4 py-3 text-sm text-destructive">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <p>Variant data is missing or invalid, so stock cannot be adjusted safely. Correct the product’s size/color rows first.</p>
          </div>
        )
      )}

      {/* Physical count input */}
      <div className="space-y-1.5">
        <label className="text-sm font-medium">
          {product.hasVariants && selectedVariant
            ? `Physical count for ${selectedVariant.label}`
            : "Physical count (what you actually have)"}
        </label>
        <Input
          type="number"
          min={0}
          step={1}
          value={physicalCount}
          onChange={e => setPhysicalCount(e.target.value)}
          className="text-lg h-12"
          autoFocus
        />
        <p className="text-xs text-muted-foreground">
          Enter the new {product.hasVariants ? "variant" : "total"} quantity as a whole number, then select Save Adjustment.
        </p>
      </div>

      {/* Difference indicator */}
      {isValid && physicalCount !== "" && (
        <div className={`flex items-center gap-2 px-4 py-3 rounded-lg text-sm font-medium ${
          diff > 0 ? "bg-green-50 text-green-700 border border-green-200"
          : diff < 0 ? "bg-red-50 text-red-700 border border-red-200"
          : "bg-muted/40 text-muted-foreground"
        }`}>
          {diff > 0 ? <TrendingUp className="w-4 h-4" /> : diff < 0 ? <TrendingDown className="w-4 h-4" /> : <Minus className="w-4 h-4" />}
          {diff === 0
            ? "No change — counts match"
            : diff > 0
            ? `Stock will increase by ${diff} ${product.unit}${diff !== 1 ? "s" : ""} (${currentStock} → ${physical})`
            : `Stock will decrease by ${Math.abs(diff)} ${product.unit}${Math.abs(diff) !== 1 ? "s" : ""} (${currentStock} → ${physical})`}
        </div>
      )}

      {/* Reason */}
      <div className="space-y-1.5">
        <label className="text-sm font-medium">Reason for adjustment</label>
        <Select value={reason} onValueChange={setReason}>
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {ADJUST_REASONS.map(r => (
              <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Notes */}
      <div className="space-y-1.5">
        <label className="text-sm font-medium text-muted-foreground">Notes <span className="font-normal">(optional)</span></label>
        <Textarea
          placeholder="e.g. Counted after closing on 23 May..."
          value={notes}
          onChange={e => setNotes(e.target.value)}
          rows={2}
        />
      </div>

      {/* Actions */}
      <div className="flex gap-2 justify-end">
        <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
        <Button
          onClick={handleSave}
          disabled={!isValid || saving || diff === 0 || done}
        >
          {done ? <><CheckCircle className="w-4 h-4 mr-2" />Saved!</>
           : saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving…</>
           : "Save Adjustment"}
        </Button>
      </div>
    </div>
  );
}

// ----- Price Adjust Dialog -----
interface PriceAdjustProduct { id: number; name: string; sku: string; price: number; costPrice: number }

function PriceAdjustDialog({ product, onClose }: { product: PriceAdjustProduct; onClose: () => void }) {
  const queryClient = useQueryClient();
  const updateProduct = useUpdateProduct();
  const [newPrice, setNewPrice] = useState<string>(String(product.price));
  const [newCost, setNewCost] = useState<string>(String(product.costPrice));
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);

  const price = parseFloat(newPrice);
  const cost = parseFloat(newCost);
  const priceValid = !isNaN(price) && price >= 0;
  const costValid = !isNaN(cost) && cost >= 0;
  const isValid = priceValid && costValid;
  const priceChanged = priceValid && price !== product.price;
  const costChanged = costValid && cost !== product.costPrice;
  const hasChange = priceChanged || costChanged;
  const margin = priceValid && costValid && price > 0 ? ((price - cost) / price * 100) : null;

  const handleSave = async () => {
    if (!isValid || !hasChange) return;
    setSaving(true);
    try {
      const data: Record<string, number> = {};
      if (priceChanged) data.price = price;
      if (costChanged) data.costPrice = cost;
      await updateProduct.mutateAsync({ id: product.id, data });
      queryClient.invalidateQueries({ queryKey: getListProductsQueryKey() });
      setDone(true);
      setTimeout(onClose, 1200);
    } catch {
      // button re-enables
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-5">
      <div className="bg-muted/40 rounded-lg px-4 py-3">
        <p className="font-semibold">{product.name}</p>
        <p className="text-xs text-muted-foreground">SKU: {product.sku}</p>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-1.5">
          <label className="text-sm font-medium">Selling Price (₦)</label>
          <p className="text-xs text-muted-foreground">Current: ₦{product.price.toFixed(2)}</p>
          <Input
            type="number"
            min={0}
            step="0.01"
            value={newPrice}
            onChange={e => setNewPrice(e.target.value)}
            className="text-lg h-12"
            autoFocus
          />
          {priceChanged && (
            <p className={`text-xs font-medium ${price > product.price ? "text-green-700" : "text-red-600"}`}>
              {price > product.price ? "▲" : "▼"} ₦{product.price.toFixed(2)} → ₦{price.toFixed(2)}
            </p>
          )}
        </div>
        <div className="space-y-1.5">
          <label className="text-sm font-medium">Cost Price (₦)</label>
          <p className="text-xs text-muted-foreground">Current: ₦{product.costPrice.toFixed(2)}</p>
          <Input
            type="number"
            min={0}
            step="0.01"
            value={newCost}
            onChange={e => setNewCost(e.target.value)}
            className="text-lg h-12"
          />
          {costChanged && (
            <p className={`text-xs font-medium ${cost < product.costPrice ? "text-green-700" : "text-orange-600"}`}>
              {cost < product.costPrice ? "▼" : "▲"} ₦{product.costPrice.toFixed(2)} → ₦{cost.toFixed(2)}
            </p>
          )}
        </div>
      </div>

      {isValid && margin !== null && (
        <div className={`flex items-center gap-2 px-4 py-3 rounded-lg text-sm font-medium ${
          margin >= 30 ? "bg-green-50 text-green-700 border border-green-200"
          : margin >= 10 ? "bg-yellow-50 text-yellow-700 border border-yellow-200"
          : "bg-red-50 text-red-700 border border-red-200"
        }`}>
          <TrendingUp className="w-4 h-4" />
          Margin: {margin.toFixed(1)}% {margin < 10 ? "— very low margin" : margin >= 30 ? "— healthy margin" : "— acceptable margin"}
        </div>
      )}

      <div className="flex gap-2 justify-end">
        <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
        <Button onClick={handleSave} disabled={!isValid || !hasChange || saving || done}>
          {done ? <><CheckCircle className="w-4 h-4 mr-2" />Saved!</>
           : saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving…</>
           : "Save Price"}
        </Button>
      </div>
    </div>
  );
}

function BulkPriceAdjustDialog({
  productCount,
  productIds,
  onClose,
  onSaved,
}: {
  productCount: number;
  productIds: number[];
  onClose: () => void;
  onSaved: (updated: number) => void;
}) {
  const { toast } = useToast();
  const [field, setField] = useState<"price" | "costPrice">("price");
  const [mode, setMode] = useState<"percentage" | "fixed">("percentage");
  const [direction, setDirection] = useState<"increase" | "decrease">("increase");
  const [adjustment, setAdjustment] = useState("");
  const [saving, setSaving] = useState(false);

  const amount = Number(adjustment);
  const valid = productIds.length > 0 && Number.isFinite(amount) && amount > 0;

  const handleSave = async () => {
    if (!valid) return;
    const signedAdjustment = direction === "increase" ? amount : -amount;
    setSaving(true);
    try {
      const response = await fetch(`${BASE}/api/products/bulk-price`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ ids: productIds, field, mode, adjustment: signedAdjustment }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || "Could not update prices");
      onSaved(Number(data.updated ?? 0));
    } catch (error) {
      toast({
        title: "Bulk price update failed",
        description: error instanceof Error ? error.message : "Please try again.",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-5">
      <div className="rounded-lg bg-muted/40 px-4 py-3">
        <p className="font-semibold">{productCount} product{productCount === 1 ? "" : "s"} selected</p>
        <p className="text-xs text-muted-foreground mt-1">
          {field === "price" ? "Selling prices" : "Cost prices"} will be updated together.
        </p>
      </div>

      <div className="space-y-1.5">
        <label className="text-sm font-medium">Price to change</label>
        <Select value={field} onValueChange={value => setField(value as "price" | "costPrice")}>
          <SelectTrigger><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="price">Selling price</SelectItem>
            <SelectItem value="costPrice">Cost price</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <label className="text-sm font-medium">Change by</label>
          <Select value={mode} onValueChange={value => setMode(value as "percentage" | "fixed")}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="percentage">Percentage</SelectItem>
              <SelectItem value="fixed">Fixed amount (₦)</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <label className="text-sm font-medium">Direction</label>
          <Select value={direction} onValueChange={value => setDirection(value as "increase" | "decrease")}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="increase">Increase</SelectItem>
              <SelectItem value="decrease">Decrease</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="space-y-1.5">
        <label className="text-sm font-medium">{mode === "percentage" ? "Percentage" : "Amount (₦)"}</label>
        <Input
          type="number"
          min="0"
          step={mode === "percentage" ? "0.1" : "0.01"}
          value={adjustment}
          onChange={event => setAdjustment(event.target.value)}
          placeholder={mode === "percentage" ? "e.g. 10" : "e.g. 500"}
          autoFocus
        />
        <p className="text-xs text-muted-foreground">Prices will never be reduced below ₦0.00.</p>
      </div>

      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
        <Button onClick={handleSave} disabled={!valid || saving}>
          {saving && <Loader2 className="w-4 h-4 animate-spin" />}
          Apply to {productCount} product{productCount === 1 ? "" : "s"}
        </Button>
      </div>
    </div>
  );
}

// ----- Import Modal -----

// Maps any reasonable column name variant → our standard field names
function normaliseColumnNames(row: any): any {
  const out: any = {};
  for (const [key, val] of Object.entries(row)) {
    // Strip currency symbols, brackets like (₦), extra spaces, then lowercase
    const k = key.trim().toLowerCase()
      .replace(/\s*\(.*?\)\s*/g, "")   // remove anything in parens e.g. "(₦)"
      .replace(/[\/\-]/g, " ")          // replace slash/dash with space so "SKU/BARCODE" → "sku barcode"
      .replace(/[^a-z0-9 _]/g, "")     // remove remaining special chars
      .replace(/\s+/g, " ")            // collapse spaces
      .trim();

    if (["name", "product name", "product", "item name", "item"].includes(k))
      out.name = val;
    else if (["sku", "barcode", "code", "product code", "item code", "ref", "reference",
              "sku barcode", "skubarcode", "sku code", "barcode sku", "sku barcode number"].includes(k))
      out.sku = val;
    else if (["price", "selling price", "unit price", "sale price", "retail price", "sell price",
               "resell price", "resale price", "market price", "sales price", "selling", "sp",
               "srp", "mrp", "list price", "customer price"].includes(k))
      out.price = val;
    else if (["costprice", "cost price", "cost", "buying price", "purchase price", "buy price", "landing cost"].includes(k))
      out.costPrice = val;
    else if (["stock", "quantity", "qty", "stock quantity", "available", "available stock",
               "inventory", "units", "in stock", "on hand", "quantity on hand", "current stock",
               "opening stock", "closing stock", "pcs", "pieces", "count", "no of units",
               "number of units", "no of pcs", "no of pieces", "bal", "balance",
               "stock balance", "remaining", "remaining stock", "total units", "total qty",
               "total quantity", "unit count", "item count"].includes(k))
      out.stock = val;
    else if (["reorderlevel", "reorder level", "reorder", "min stock", "minimum stock", "min qty"].includes(k))
      out.reorderLevel = val;
    else if (["unit", "unit of measure", "uom", "measure"].includes(k))
      out.unit = val;
    else if (["description", "notes", "details", "product description", "note"].includes(k))
      out.description = val;
    else if (["category", "category name", "dept", "department"].includes(k))
      out.category = val;
    else if (["brand", "brand name", "manufacturer", "make"].includes(k))
      out.brand = val;
    else if (["product type", "type", "item type"].includes(k))
      out.productType = val;
    else if (["parent sku", "parent code", "group sku"].includes(k))
      out.parentSku = val;
    else if (["variant name", "variation name", "option", "variant"].includes(k))
      out.variantName = val;
    else if (["variant sku", "variation sku", "option sku"].includes(k))
      out.variantSku = val;
    else if (["date added", "date created", "created at"].includes(k))
      {} // skip — read-only metadata
    else
      out[key] = val; // preserve unknown columns as-is
  }
  return out;
}

function normaliseRows(raw: any[], rowLimit: number): { rows: any[]; autoNameCount: number; autoSkuCount: number; totalMeaningful: number } {
  // Filter out completely blank rows (e.g. spacer rows with no product name, sku, or price)
  const nonEmpty = raw.filter(r => {
    const vals = Object.values(r).map(v => String(v ?? "").trim());
    return vals.some(v => v !== "");
  });
  // Further filter: keep only rows that have at least a name OR a sku OR a non-zero price
  const meaningful = nonEmpty.filter(r => {
    const nameKey = Object.keys(r).find(k => /^(product|name|item)/i.test(k));
    const skuKey  = Object.keys(r).find(k => /sku|barcode|code/i.test(k));
    const priceKey = Object.keys(r).find(k => /price|selling/i.test(k));
    const name  = nameKey  ? String(r[nameKey]  ?? "").trim() : "";
    const sku   = skuKey   ? String(r[skuKey]   ?? "").trim() : "";
    const price = priceKey ? String(r[priceKey] ?? "").trim() : "";
    return name !== "" || sku !== "" || price !== "";
  });
  const totalMeaningful = meaningful.length;
  const capped = meaningful.slice(0, rowLimit);
  let autoNameCount = 0;
  let autoSkuCount = 0;
  const ts = Date.now();
  const rows = capped.map((r, i) => {
    const out = normaliseColumnNames(r);

    out._providedFields = Object.keys(out).filter(k => out[k] !== "" && out[k] !== null && out[k] !== undefined);

    if (!out.name || String(out.name).trim() === "") {
      out.name = `Unnamed Product ${i + 1}`;
      out._autoName = true;
      autoNameCount++;
    }
    if (!out.sku || String(out.sku).trim() === "") {
      out.sku = `IMP-${ts}-${String(i + 1).padStart(3, "0")}`;
      out._autoSku = true;
      autoSkuCount++;
    }
    if (!out.price && out.price !== 0) {
      out.price = 0;
      out._autoPrice = true;
    }
    return out;
  });
  return { rows, autoNameCount, autoSkuCount, totalMeaningful };
}

const ACCEPTED_COLUMNS = [
  { label: "Product Name", values: ["name", "product name", "product", "item name", "item"] },
  { label: "SKU / Barcode", values: ["sku", "barcode", "code", "product code", "item code", "ref", "reference"] },
  { label: "Selling Price ★", values: ["price", "selling price", "unit price", "sale price", "retail price", "sell price", "resell price", "resale price", "market price", "sales price", "sp", "srp", "mrp", "list price", "customer price"] },
  { label: "Cost Price", values: ["cost price", "cost", "costprice", "buying price", "purchase price", "buy price", "landing cost"] },
  { label: "Stock / Qty", values: ["stock", "quantity", "qty", "stock quantity", "available", "available stock", "inventory", "units", "in stock", "on hand", "quantity on hand", "current stock", "opening stock", "closing stock", "pcs", "pieces", "count", "no of units", "number of units", "no of pcs", "no of pieces", "bal", "balance", "stock balance", "remaining", "remaining stock", "total units", "total qty", "total quantity", "unit count", "item count"] },
  { label: "Reorder Level", values: ["reorder level", "reorder", "min stock", "minimum stock", "min qty"] },
  { label: "Unit", values: ["unit", "unit of measure", "uom", "measure"] },
  { label: "Description", values: ["description", "notes", "details", "product description", "note"] },
  { label: "Category", values: ["category", "category name", "dept", "department"] },
  { label: "Brand", values: ["brand", "brand name", "manufacturer", "make"] },
  { label: "Product Type", values: ["product type", "type", "item type"] },
  { label: "Parent SKU", values: ["parent sku", "parent code", "group sku"] },
  { label: "Variant Name", values: ["variant name", "variation name", "option", "variant"] },
  { label: "Variant SKU", values: ["variant sku", "variation sku", "option sku"] },
];

type ImportMode = "create" | "update" | "upsert";
type ProductEntryMethod = "file" | "grid";

type ImportResult = {
  success: number;
  createdCount: number;
  updatedCount: number;
  skipped: number;
  failed: number;
  errors: string[];
};

type ProductImportJob = ImportResult & {
  id: number;
  status: "queued" | "processing" | "completed" | "failed";
  mode: ImportMode;
  fileName: string | null;
  includesVariants: boolean;
  totalRows: number;
  processedRows: number;
  completedRows: number;
  error: string | null;
  createdAt: string;
};

function ProductImportSummary({ job, onDismiss }: { job: ProductImportJob; onDismiss: () => void }) {
  const isActive = job.status === "queued" || job.status === "processing";
  const needsPlanUpgrade = job.error?.toLowerCase().includes("product limit reached") ?? false;
  const progress = job.totalRows > 0 ? Math.min(100, (job.processedRows / job.totalRows) * 100) : 0;
  return (
    <section
      className={`rounded-xl border p-4 sm:p-5 ${
        job.status === "failed"
          ? "border-destructive/30 bg-destructive/5"
          : job.status === "completed"
            ? "border-emerald-200 bg-emerald-50/40"
            : "border-primary/20 bg-primary/5"
      }`}
      aria-live="polite"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 className="font-semibold">
            {job.status === "completed"
              ? "Product import complete"
              : job.status === "failed"
                ? "Product import stopped"
                : "Product import in progress"}
          </h2>
          <p className="text-sm text-muted-foreground mt-1">
            {job.fileName ? `${job.fileName} · ` : ""}
            {job.processedRows.toLocaleString()} of {job.totalRows.toLocaleString()} rows processed
          </p>
        </div>
        {!isActive && (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            aria-label="Dismiss import summary"
            onClick={onDismiss}
          >
            <X className="w-4 h-4" />
          </Button>
        )}
      </div>

      {isActive ? (
        <div
          className="w-full h-2 rounded-full bg-background/80 mt-4 overflow-hidden"
          role="progressbar"
          aria-label="Product import progress"
          aria-valuemin={0}
          aria-valuemax={job.totalRows}
          aria-valuenow={job.processedRows}
        >
          <div
            className="h-full rounded-full bg-primary transition-[width] duration-300"
            style={{ width: `${progress}%` }}
          />
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mt-4">
          {[
            { label: "Completed rows", value: job.completedRows },
            { label: "Created", value: job.createdCount },
            { label: "Updated", value: job.updatedCount },
            { label: "Skipped rows", value: job.skipped },
            { label: "Failed rows", value: job.failed },
          ].map(item => (
            <div key={item.label} className="rounded-lg border bg-background/80 px-3 py-2">
              <p className="text-xs text-muted-foreground">{item.label}</p>
              <p className="text-lg font-semibold">{item.value.toLocaleString()}</p>
            </div>
          ))}
        </div>
      )}

      {job.error && <p className="text-sm text-destructive mt-3">{job.error}</p>}
      {needsPlanUpgrade && (
        <Link href="/subscribe">
          <Button type="button" size="sm" className="mt-3">View plans</Button>
        </Link>
      )}
      {job.errors.length > 0 && (
        <details className="mt-3">
          <summary className="cursor-pointer text-sm font-medium">
            View row errors and warnings ({job.errors.length})
          </summary>
          <div className="mt-2 max-h-48 overflow-y-auto rounded-lg border bg-background/80 p-3 text-xs space-y-1.5">
            {job.errors.map((error, index) => (
              <p key={`${index}-${error}`} className="border-b last:border-0 py-1.5 text-muted-foreground">
                {error}
              </p>
            ))}
          </div>
        </details>
      )}
    </section>
  );
}

function ImportModal({
  onClose,
  importJob,
  onJobUpdate,
  initialEntryMethod,
}: {
  onClose: () => void;
  importJob: ProductImportJob | null;
  onJobUpdate: (job: ProductImportJob) => void;
  initialEntryMethod: ProductEntryMethod;
}) {
  const { store } = useAuth();
  const { toast } = useToast();
  const { activeBranchId } = useBranch();
  const fileRef = useRef<HTMLInputElement>(null);
  const trackedJobId = useRef<number | null>(null);
  const [rawRows, setRawRows] = useState<any[]>([]);
  const [fileName, setFileName] = useState("");
  const [startingImport, setStartingImport] = useState(false);
  const [currentJobId, setCurrentJobId] = useState<number | null>(null);
  const [confirmed, setConfirmed] = useState(false);
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [entryMethod, setEntryMethod] = useState<ProductEntryMethod>(initialEntryMethod);
  const [mode, setMode] = useState<ImportMode>(initialEntryMethod === "grid" ? "create" : "upsert");
  const [manualRows, setManualRows] = useState<ManualProductEntryRow[]>(() =>
    Array.from({ length: 5 }, () => createManualProductEntryRow())
  );
  const [parseError, setParseError] = useState("");
  const [barcodePreview, setBarcodePreview] = useState<ProductImportBarcodePreview | null>(null);
  const [barcodePreviewLoading, setBarcodePreviewLoading] = useState(false);
  const [barcodePreviewError, setBarcodePreviewError] = useState("");
  const [barcodePreviewRetry, setBarcodePreviewRetry] = useState(0);
  const currentJob = importJob?.id === currentJobId ? importJob : null;
  const importing = startingImport || currentJob?.status === "queued" || currentJob?.status === "processing";
  const result = currentJob?.status === "completed" || currentJob?.status === "failed" ? currentJob : null;

  useEffect(() => {
    if (currentJobId === null && importJob && (importJob.status === "queued" || importJob.status === "processing")) {
      setCurrentJobId(importJob.id);
      setStep(3);
    }
  }, [currentJobId, importJob]);

  useEffect(() => {
    if (result?.status !== "completed" || trackedJobId.current === result.id) return;
    trackedJobId.current = result.id;
    trackEvent("products_imported", {
      mode: result.mode,
      imported_count: result.createdCount + result.updatedCount,
      failed_count: result.failed,
      includes_variants: result.includesVariants,
      file_type: result.fileName?.split(".").pop()?.toLowerCase() ?? "unknown",
    });
  }, [result?.id, result?.status]);

  const isEnterprise = store?.plan === "enterprise";
  const importRowLimit = store?.plan === "enterprise" ? Infinity : store?.plan === "pro" ? 2500 : store?.plan === "basic" ? 1500 : 1000;

  useEffect(() => {
    if (step !== 2 || preview.length === 0) {
      setBarcodePreviewLoading(false);
      return;
    }

    let active = true;
    setBarcodePreview(null);
    setBarcodePreviewError("");
    setBarcodePreviewLoading(true);
    void fetch(`${BASE}/api/products/import-preview`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({
        products: preview,
        mode,
        branchId: activeBranchId ?? null,
      }),
    })
      .then(async response => {
        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
          throw new Error(typeof data.error === "string" ? data.error : `Barcode preview failed (${response.status}).`);
        }
        return data as ProductImportBarcodePreview;
      })
      .then(data => {
        if (active) setBarcodePreview(data);
      })
      .catch(error => {
        if (active) {
          setBarcodePreviewError(error instanceof Error ? error.message : "Could not check barcode conflicts.");
        }
      })
      .finally(() => {
        if (active) setBarcodePreviewLoading(false);
      });

    return () => {
      active = false;
    };
  }, [step, rawRows, mode, activeBranchId, importRowLimit, barcodePreviewRetry]);

  const downloadTemplate = () => {
    const simpleRows = [
      {
        "Product Name": "Peak Milk 400g",
        "Product Type": "Simple",
        "Parent SKU": "",
        "Variant Name": "",
        "Variant SKU": "",
        "SKU / Barcode": "PEAK-400",
        "Selling Price": 2500,
        "Cost Price": 2100,
        "Stock": 24,
        "Reorder Level": 5,
        "Unit": "piece",
        "Category": "Groceries",
        "Brand": "Peak",
        "Description": "Example simple product",
      },
    ];
    const variableRows = [
      {
        "Product Name": "Classic T-Shirt",
        "Product Type": "Variable",
        "Parent SKU": "TSHIRT-001",
        "Variant Name": "Small / Black",
        "Variant SKU": "TSHIRT-001-S-BLK",
        "SKU / Barcode": "",
        "Selling Price": 8000,
        "Cost Price": 5000,
        "Stock": 10,
        "Reorder Level": 3,
        "Unit": "piece",
        "Category": "Clothing",
        "Brand": "RetailPulse",
        "Description": "One row per size or colour",
      },
      {
        "Product Name": "Classic T-Shirt",
        "Product Type": "Variable",
        "Parent SKU": "TSHIRT-001",
        "Variant Name": "Medium / Black",
        "Variant SKU": "TSHIRT-001-M-BLK",
        "SKU / Barcode": "",
        "Selling Price": 8500,
        "Cost Price": 5200,
        "Stock": 7,
        "Reorder Level": 3,
        "Unit": "piece",
        "Category": "Clothing",
        "Brand": "RetailPulse",
        "Description": "Rows sharing a Parent SKU become one product",
      },
    ];
    const instructions = [
      ["RetailPulsePOS Product Import Instructions"],
      [""],
      ["Simple products", "Use one row per product. Product Type may be Simple or left blank."],
      ["Variable products", "Use one row per variant and set Product Type to Variable."],
      ["Grouping", "Every variant of the same product must use the same Parent SKU."],
      ["Required for variable products", "Product Name, Product Type, Parent SKU, Variant Name, Variant SKU, Selling Price, Stock."],
      ["Optional columns included", "Cost Price, Reorder Level, Unit, Category, Brand, Description."],
      ["Important", "Variant SKU must be unique. Do not reuse a Parent SKU for different products."],
    ];
    const wb = XLSX.utils.book_new();
    const simpleSheet = XLSX.utils.json_to_sheet(simpleRows);
    const variableSheet = XLSX.utils.json_to_sheet(variableRows);
    const instructionSheet = XLSX.utils.aoa_to_sheet(instructions);
    simpleSheet["!cols"] = Array(14).fill({ wch: 18 });
    variableSheet["!cols"] = Array(14).fill({ wch: 20 });
    instructionSheet["!cols"] = [{ wch: 30 }, { wch: 90 }];
    XLSX.utils.book_append_sheet(wb, instructionSheet, "Instructions");
    XLSX.utils.book_append_sheet(wb, simpleSheet, "Simple Products");
    XLSX.utils.book_append_sheet(wb, variableSheet, "Variable Products");
    XLSX.writeFile(wb, "RetailPulsePOS_Product_Import_Template.xlsx");
  };

  const handleFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setEntryMethod("file");
    setFileName(file.name);
    setConfirmed(false);
    setParseError("");
    setBarcodePreview(null);
    setBarcodePreviewError("");

    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const data = new Uint8Array(ev.target!.result as ArrayBuffer);
        const workbook = XLSX.read(data, { type: "array" });
        const sheet = workbook.Sheets[workbook.SheetNames[0]];
        if (!sheet) throw new Error("Spreadsheet is empty or invalid.");

        const rows = XLSX.utils.sheet_to_json(sheet, { defval: "" }) as any[];
        if (!rows.length) throw new Error("No data found in the spreadsheet.");

        setRawRows(rows);
        setStep(2); // Auto-advance to review on successful parse
      } catch (err) {
        setParseError(err instanceof Error ? err.message : "Failed to parse file.");
        setRawRows([]);
      } finally {
        if (fileRef.current) fileRef.current.value = "";
      }
    };
    reader.onerror = () => {
      setParseError("Could not read file.");
      if (fileRef.current) fileRef.current.value = "";
    };
    reader.readAsArrayBuffer(file);
  };

  const handleManualReview = (rows: Record<string, string>[]) => {
    setEntryMethod("grid");
    setMode("create");
    setRawRows(rows);
    setFileName("Manual product entry");
    setConfirmed(false);
    setBarcodePreview(null);
    setBarcodePreviewError("");
    setStep(2);
  };

  const selectEntryMethod = (method: ProductEntryMethod) => {
    setEntryMethod(method);
    setStep(1);
    setRawRows([]);
    setFileName("");
    setConfirmed(false);
    setBarcodePreview(null);
    setBarcodePreviewError("");
    if (method === "grid") setMode("create");
  };

  const { rows: preview, autoNameCount, autoSkuCount, totalMeaningful = 0 } = rawRows.length > 0
    ? normaliseRows(rawRows, importRowLimit)
    : { rows: [], autoNameCount: 0, autoSkuCount: 0, totalMeaningful: 0 };

  const requirePrice = mode === "create" || mode === "upsert";
  const hasPriceCol = preview.length > 0 && preview.some(r => r._providedFields?.includes("price"));
  const hasPartialData = autoNameCount > 0 || autoSkuCount > 0;
  const needsConfirmation = hasPartialData && (mode === "create" || mode === "upsert");
  const barcodeConflictCount = barcodePreview?.conflicts.length ?? 0;
  const importValidationErrors = barcodePreview?.validationErrors ?? [];
  const canImport = (!requirePrice || hasPriceCol) &&
    (needsConfirmation ? confirmed : true) &&
    !barcodePreviewLoading &&
    !barcodePreviewError &&
    barcodePreview !== null &&
    barcodeConflictCount === 0 &&
    importValidationErrors.length === 0;

  const handleImport = async () => {
    if (!preview.length || !canImport) return;
    setStep(3);
    setStartingImport(true);
    try {
      const res = await fetch(`${BASE}/api/products/import-jobs`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          products: preview,
          mode,
          branchId: activeBranchId,
          fileName,
          includesVariants: preview.some(row =>
            String(row.productType ?? "").toLowerCase() === "variable" ||
            Boolean(row.parentSku || row.variantName || row.variantSku)
          ),
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok && data.upgrade === true) {
        setStep(2);
        toast({
          title: "Product limit reached",
          description: data.error ?? "Upgrade your plan to add more products.",
          action: <ToastAction altText="View plans" onClick={() => { window.location.href = `${BASE}/subscribe`; }}>View plans</ToastAction>,
        });
        return;
      }
      if (data.job) {
        onJobUpdate(data.job as ProductImportJob);
        setCurrentJobId(data.job.id);
        setStep(3);
      }
      if (!res.ok && !data.job) {
        throw new Error(data.error ?? `The import could not be started (${res.status}).`);
      }
      if (!res.ok) {
        toast({
          title: "Another import is already running",
          description: data.error ?? "Showing the import already in progress for this store.",
        });
        return;
      }
      const job = data as ProductImportJob;
      onJobUpdate(job);
      setCurrentJobId(job.id);
    } catch (error) {
      try {
        const latestResponse = await fetch(`${BASE}/api/products/import-jobs/latest`, { credentials: "include" });
        const latestData = await latestResponse.json().catch(() => ({}));
        const latestJob = latestData.job as ProductImportJob | null;
        if (latestResponse.ok && latestJob && (latestJob.status === "queued" || latestJob.status === "processing")) {
          onJobUpdate(latestJob);
          setCurrentJobId(latestJob.id);
          setStep(3);
          return;
        }
      } catch {
        // Keep the original start error visible if status recovery also fails.
      }
      setStep(2);
      toast({
        title: "Import could not be started",
        description: error instanceof Error ? error.message : "Check your connection and try again.",
        variant: "destructive",
      });
    } finally {
      setStartingImport(false);
    }
  };

  return (
    <div className="flex flex-col h-full max-h-[85vh]">
      <div className="flex-1 overflow-y-auto px-1 py-2">
        {!isEnterprise && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 flex items-start gap-3 text-sm mb-4">
            <Crown className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
            <div>
              <span className="font-medium text-amber-900">
                {`${(store?.plan ?? "trial").charAt(0).toUpperCase()}${(store?.plan ?? "trial").slice(1)} plan:`}
              </span>{" "}
              <span className="text-amber-700">
                Import is limited to {Number(importRowLimit).toLocaleString("en-NG")} rows per batch. Upgrade to Enterprise for unlimited bulk import.
              </span>
            </div>
          </div>
        )}

        {step === 1 && (
          <div className="space-y-5">
            <div className="grid grid-cols-2 gap-2 rounded-xl border bg-muted/30 p-1" role="group" aria-label="Product entry method">
              <Button
                type="button"
                variant={entryMethod === "grid" ? "default" : "ghost"}
                data-testid="button-manual-product-entry"
                aria-pressed={entryMethod === "grid"}
                onClick={() => selectEntryMethod("grid")}
              >
                Enter or paste rows
              </Button>
              <Button
                type="button"
                variant={entryMethod === "file" ? "default" : "ghost"}
                data-testid="button-spreadsheet-product-import"
                aria-pressed={entryMethod === "file"}
                onClick={() => selectEntryMethod("file")}
              >
                Upload CSV / Excel
              </Button>
            </div>

            {entryMethod === "grid" && (
              <ManualProductEntryGrid
                rows={manualRows}
                rowLimit={importRowLimit}
                onRowsChange={setManualRows}
                onReview={handleManualReview}
              />
            )}

            {entryMethod === "file" && (
              <div className="space-y-6">
            <div>
              <h3 className="text-sm font-semibold mb-3 text-foreground/90">1. Choose how to handle the imported products</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {[
                  { id: "create", title: "Add new products", desc: "Only creates new items. Existing ones are ignored." },
                  { id: "update", title: "Update existing", desc: "Updates stock and prices for items you already have." },
                  { id: "upsert", title: "Add & Update", desc: "Adds new items and updates existing ones." }
                ].map(m => (
                  <div
                    key={m.id}
                    onClick={() => setMode(m.id as ImportMode)}
                    className={`border rounded-xl p-4 cursor-pointer transition-all ${mode === m.id ? 'border-primary bg-primary/5 ring-1 ring-primary shadow-sm' : 'hover:border-primary/50 bg-card hover:bg-muted/30'}`}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <div className={`w-4 h-4 rounded-full border flex items-center justify-center flex-shrink-0 ${mode === m.id ? 'border-primary' : 'border-muted-foreground'}`}>
                        {mode === m.id && <div className="w-2 h-2 rounded-full bg-primary" />}
                      </div>
                      <span className="font-semibold text-sm">{m.title}</span>
                    </div>
                    <p className="text-xs text-muted-foreground ml-6 leading-relaxed">{m.desc}</p>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <h3 className="text-sm font-semibold mb-3 text-foreground/90">2. Upload your spreadsheet</h3>
              <div
                className="border-2 border-dashed border-border rounded-xl p-8 text-center cursor-pointer hover:bg-muted/30 transition-colors"
                onClick={() => fileRef.current?.click()}
              >
                <FileSpreadsheet className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
                <p className="font-medium">Click to select CSV or Excel file</p>
                <p className="text-xs text-muted-foreground mt-1">
                  {mode === "update" ? (
                    <>Update by mapping <strong>SKU / Barcode</strong> to the fields you want to change.</>
                  ) : (
                    <>Make sure your file has a <strong>price</strong> column.</>
                  )}
                </p>
                <input ref={fileRef} type="file" accept=".csv,.xlsx,.xls" className="hidden" onChange={handleFile} />
              </div>
              {parseError && (
                 <div className="mt-3 text-sm text-destructive bg-destructive/10 px-4 py-3 rounded-lg flex items-center gap-2">
                    <XCircle className="w-4 h-4 flex-shrink-0" />
                    {parseError}
                 </div>
              )}
            </div>

            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-muted/30 rounded-xl p-5 border">
              <div>
                <p className="text-sm font-semibold">Need a starting point?</p>
                <p className="text-xs text-muted-foreground mt-1">Download our template with examples for simple and variable products.</p>
              </div>
              <Button type="button" variant="outline" size="sm" className="gap-2 shrink-0 bg-background" onClick={downloadTemplate}>
                <Download className="w-4 h-4" /> Download Template
              </Button>
            </div>

            <div className="bg-blue-50/60 border border-blue-100 rounded-xl p-5">
              <h4 className="text-sm font-semibold text-blue-900 mb-2 flex items-center gap-2">
                <Info className="w-4 h-4" /> SKU vs Barcode
              </h4>
              <div className="space-y-3">
                <p className="text-xs text-blue-800 leading-relaxed">
                  <strong>SKU (Stock Keeping Unit):</strong> Your internal code for a product (e.g. TSHIRT-BLK-S). If you don't provide one, we'll generate it automatically.
                </p>
                <p className="text-xs text-blue-800 leading-relaxed">
                  <strong>Barcode:</strong> The scannable numbers on the product packaging. Use this if you scan items at checkout.
                </p>
              </div>
            </div>
              </div>
            )}
          </div>
        )}

        {step === 2 && (
          <div className="space-y-5">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold">Review your data</h3>
              <Button
                variant="ghost"
                size="sm"
                data-testid="button-return-to-product-entry"
                onClick={() => {
                  setStep(1);
                  setRawRows([]);
                  setConfirmed(false);
                  setBarcodePreview(null);
                  setBarcodePreviewError("");
                }}
                className="gap-1.5 h-8 -mr-2"
              >
                <ChevronLeft className="w-4 h-4" /> Back to {entryMethod === "grid" ? "grid" : "file"}
              </Button>
            </div>

            {!hasPriceCol && requirePrice ? (
              <div className="text-sm text-destructive bg-destructive/10 px-4 py-3 rounded-lg flex items-center gap-2 border border-destructive/20">
                <XCircle className="w-4 h-4 flex-shrink-0" />
                {entryMethod === "grid"
                  ? <>Enter a selling price for at least one product, then review the grid again.</>
                  : <>Missing required column: <strong>price</strong>. Please add a price column to your file and try again.</>}
              </div>
            ) : (
              <>
                <div className="bg-muted/30 rounded-xl p-4 border grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                   <div>
                     <p className="text-muted-foreground mb-1 text-xs uppercase tracking-wider">Ready to import</p>
                     <p className="font-bold text-xl">{preview.length}</p>
                   </div>
                   {!isEnterprise && totalMeaningful > importRowLimit && (
                     <div>
                       <p className="text-amber-600 mb-1 text-xs uppercase tracking-wider">Trimmed (Limit)</p>
                       <p className="font-bold text-xl text-amber-700">{totalMeaningful - importRowLimit}</p>
                     </div>
                   )}
                   <div className="col-span-2">
                     <p className="text-muted-foreground mb-1 text-xs uppercase tracking-wider">Mode</p>
                     <p className="font-medium mt-0.5 inline-flex items-center bg-background border px-2.5 py-1 rounded text-xs shadow-sm">
                       {mode === 'create' ? 'Add new products' : mode === 'update' ? 'Update existing' : 'Add & Update'}
                     </p>
                   </div>
                </div>

                {needsConfirmation && (
                  <div className="bg-amber-50 border border-amber-200 rounded-xl px-5 py-4 space-y-3 text-sm shadow-sm">
                    <div className="flex items-start gap-3">
                      <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                      <div>
                        <div className="font-semibold text-amber-900 mb-2">Some fields are missing in your file</div>
                        <ul className="space-y-1.5">
                          {autoNameCount > 0 && <li className="text-amber-700 flex gap-2"><span className="text-amber-400">•</span> <strong>{autoNameCount}</strong> products have no name — will be imported as "Unnamed Product N"</li>}
                          {autoSkuCount > 0 && <li className="text-amber-700 flex gap-2"><span className="text-amber-400">•</span> <strong>{autoSkuCount}</strong> products have no SKU — a unique SKU will be auto-generated</li>}
                        </ul>
                      </div>
                    </div>

                    <label className="flex items-center gap-3 ml-8 mt-4 cursor-pointer p-2 hover:bg-amber-100/50 rounded-lg transition-colors w-fit border border-transparent hover:border-amber-200/60">
                      <input type="checkbox" checked={confirmed} onChange={e => setConfirmed(e.target.checked)} className="w-4 h-4 rounded border-amber-300 text-amber-600 focus:ring-amber-500" />
                      <span className="text-amber-900 font-medium">I understand — import anyway</span>
                    </label>
                  </div>
                )}

                {barcodePreviewLoading && (
                  <div className="flex items-center gap-2 rounded-xl border bg-muted/30 px-4 py-3 text-sm text-muted-foreground" role="status">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Checking product and variant SKUs against your store and this file…
                  </div>
                )}

                {barcodePreviewError && (
                  <div className="rounded-xl border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm" role="alert">
                    <div className="flex items-start gap-2 text-destructive">
                      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                      <div className="flex-1">
                        <p className="font-semibold">Import preview could not be completed</p>
                        <p className="mt-1">{barcodePreviewError}</p>
                      </div>
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="mt-3"
                      onClick={() => setBarcodePreviewRetry(value => value + 1)}
                    >
                      Retry preview
                    </Button>
                  </div>
                )}

                {!barcodePreviewLoading && !barcodePreviewError && importValidationErrors.length > 0 && (
                  <div className="rounded-xl border border-destructive/30 bg-destructive/5 p-4 text-sm" role="alert">
                    <div className="flex items-start gap-2">
                      <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-destructive" />
                      <div>
                        <p className="font-semibold text-destructive">
                          {importValidationErrors.length} invalid product {importValidationErrors.length === 1 ? "value" : "values"} found
                        </p>
                        <p className="mt-1 text-sm text-muted-foreground">
                          Correct these rows before importing. Use Back to {entryMethod === "grid" ? "grid" : "file"} to make changes, then review again.
                        </p>
                      </div>
                    </div>
                    <ul className="mt-3 max-h-56 space-y-1 overflow-y-auto rounded-lg border bg-background p-3 text-sm">
                      {importValidationErrors.slice(0, 30).map((error, index) => (
                        <li key={`${error}-${index}`}>{error}</li>
                      ))}
                      {importValidationErrors.length > 30 && (
                        <li className="pt-1 text-muted-foreground">
                          Showing the first 30 of {importValidationErrors.length} validation errors.
                        </li>
                      )}
                    </ul>
                  </div>
                )}

                {!barcodePreviewLoading && !barcodePreviewError && barcodePreview && barcodeConflictCount === 0 && (
                  <div className="rounded-xl border border-emerald-200 bg-emerald-50/60 px-4 py-3 text-sm text-emerald-800">
                    No product or variant barcode conflicts found. The import will still recheck conflicts when it saves.
                  </div>
                )}

                {!barcodePreviewLoading && !barcodePreviewError && barcodeConflictCount > 0 && (
                  <div className="rounded-xl border border-destructive/30 bg-destructive/5 p-4" role="alert">
                    <div className="flex items-start gap-2">
                      <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-destructive" />
                      <div>
                        <p className="font-semibold text-destructive">
                          {barcodeConflictCount} barcode {barcodeConflictCount === 1 ? "conflict" : "conflicts"} found
                        </p>
                        <p className="mt-1 text-sm text-muted-foreground">
                          {entryMethod === "grid"
                            ? "Go back to the grid and correct these SKUs before adding products."
                            : "Correct these SKUs in your file and upload it again. Import is disabled until the file is conflict-free."}
                        </p>
                      </div>
                    </div>
                    <ul className="mt-3 max-h-56 space-y-3 overflow-y-auto rounded-lg border bg-background p-3 text-sm">
                      {barcodePreview?.conflicts.map((conflict, conflictIndex) => (
                        <li key={`${conflict.sku}-${conflictIndex}`} className="border-b pb-3 last:border-0 last:pb-0">
                          <p className="font-mono font-semibold">{conflict.sku}</p>
                          <ul className="mt-1 space-y-1 text-muted-foreground">
                            {conflict.items.map((item, itemIndex) => {
                              const sourceLabel = item.source === "catalog"
                                ? "Already in this store"
                                : `File ${item.rowNumbers?.length === 1 ? "row" : "rows"} ${(item.rowNumbers ?? []).join(", ")}`;
                              const kindLabel = item.itemType === "variant"
                                ? `variant${item.variantLabel ? ` “${item.variantLabel}”` : ""}`
                                : "product";
                              return (
                                <li key={`${item.source}-${item.productId ?? item.productName}-${itemIndex}`}>
                                  {sourceLabel}: {item.productName} ({kindLabel})
                                </li>
                              );
                            })}
                          </ul>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {mode === "update" && autoSkuCount > 0 && (
                  <div className="text-sm text-amber-800 bg-amber-50 px-4 py-3 rounded-xl border border-amber-200 flex items-start gap-3">
                    <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5 text-amber-600" />
                    <div>
                      <p className="font-semibold text-amber-900 mb-0.5">Missing SKUs</p>
                      <p className="text-xs text-amber-700 leading-relaxed"><strong>{autoSkuCount}</strong> rows have no SKU or Barcode. They will be skipped since we cannot find which product to update.</p>
                    </div>
                  </div>
                )}

                <div className="overflow-auto max-h-[300px] border rounded-xl text-xs shadow-sm">
                   <table className="w-full">
                      <thead className="bg-muted/80 sticky top-0 shadow-sm backdrop-blur-sm z-10">
                        <tr>
                          {["Row", "name", "productType", "parentSku", "variantName", "variantSku", "price", "stock", "category"].map(k => (
                            <th key={k} className="px-4 py-2.5 text-left font-semibold text-muted-foreground">{k}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {preview.slice(0, 15).map((row, i) => (
                          <tr key={i} className={`transition-colors ${(row._autoName || row._autoSku) ? "bg-amber-50/30 hover:bg-amber-50/60" : "hover:bg-muted/30"}`}>
                            <td className="px-4 py-2 text-muted-foreground">{i + 1}</td>
                            {["name", "productType", "parentSku", "variantName", "variantSku", "price", "stock", "category"].map((k, j) => (
                              <td key={j} className="px-4 py-2 text-foreground/80">{String(row[k] ?? "").slice(0, 28)}</td>
                            ))}
                          </tr>
                        ))}
                        {preview.length > 15 && (
                            <tr><td colSpan={9} className="px-4 py-3 text-center text-muted-foreground italic bg-muted/10 font-medium">...and {preview.length - 15} more rows</td></tr>
                        )}
                      </tbody>
                    </table>
                </div>
              </>
            )}
          </div>
        )}

        {step === 3 && (
          <div className="space-y-6 py-2">
            {importing ? (
              <div className="py-16 flex flex-col items-center">
                <Loader2 className="w-12 h-12 text-primary animate-spin mb-5" />
                <h3 className="text-xl font-semibold mb-2">Importing products...</h3>
                <p className="text-sm text-muted-foreground">
                  {currentJob
                    ? `${currentJob.processedRows.toLocaleString()} of ${currentJob.totalRows.toLocaleString()} rows processed`
                    : "Preparing your import..."}
                </p>
                {currentJob && (
                  <div
                    className="w-full max-w-sm h-2 rounded-full bg-muted mt-4 overflow-hidden"
                    role="progressbar"
                    aria-label="Product import progress"
                    aria-valuemin={0}
                    aria-valuemax={currentJob.totalRows}
                    aria-valuenow={currentJob.processedRows}
                  >
                    <div
                      className="h-full rounded-full bg-primary transition-[width] duration-300"
                      style={{ width: `${Math.min(100, (currentJob.processedRows / currentJob.totalRows) * 100)}%` }}
                    />
                  </div>
                )}
                <p className="text-xs text-muted-foreground mt-3">
                  You can close this window; the import will continue in the background.
                </p>
              </div>
            ) : result ? (
              <div className="text-left space-y-6">
                <div className={`rounded-2xl p-6 border shadow-sm ${result.status === "completed" ? "bg-emerald-50/30 border-emerald-100" : "bg-destructive/5 border-destructive/10"}`}>
                  <div className="flex items-center gap-3 font-semibold text-xl mb-6">
                    {result.status === "completed" ? <CheckCircle className="w-7 h-7 text-emerald-500" /> : <XCircle className="w-7 h-7 text-destructive/80" />}
                    {result.status === "completed" ? "Import Complete" : "Import Failed"}
                  </div>

                  <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-2">
                    <div className="bg-background rounded-xl p-4 border border-primary/10 shadow-sm">
                       <p className="text-[10px] text-muted-foreground font-bold uppercase tracking-widest mb-1.5">Completed rows</p>
                        <p className="text-3xl font-bold text-primary">{result.completedRows || 0}</p>
                    </div>
                    <div className="bg-background rounded-xl p-4 border border-emerald-100 shadow-sm">
                       <p className="text-[10px] text-muted-foreground font-bold uppercase tracking-widest mb-1.5">Created</p>
                        <p className="text-3xl font-bold text-emerald-600">{result.createdCount || 0}</p>
                    </div>
                    <div className="bg-background rounded-xl p-4 border border-blue-100 shadow-sm">
                       <p className="text-[10px] text-muted-foreground font-bold uppercase tracking-widest mb-1.5">Updated</p>
                        <p className="text-3xl font-bold text-blue-600">{result.updatedCount || 0}</p>
                    </div>
                    <div className="bg-background rounded-xl p-4 border border-amber-100 shadow-sm">
                       <p className="text-[10px] text-muted-foreground font-bold uppercase tracking-widest mb-1.5">Skipped rows</p>
                       <p className="text-3xl font-bold text-amber-600">{result.skipped || 0}</p>
                    </div>
                    <div className="bg-background rounded-xl p-4 border border-destructive/20 shadow-sm">
                       <p className="text-[10px] text-muted-foreground font-bold uppercase tracking-widest mb-1.5">Failed rows</p>
                       <p className="text-3xl font-bold text-destructive/80">{result.failed || 0}</p>
                    </div>
                  </div>
                  {result.error && <p className="mt-4 text-sm text-destructive">{result.error}</p>}
                </div>

                {result.errors && result.errors.length > 0 && (
                  <div>
                    <h4 className="font-semibold text-sm mb-3 flex items-center gap-2 text-foreground/90">
                       <AlertTriangle className="w-4 h-4 text-destructive" />
                       Errors & Warnings
                    </h4>
                    <div className="max-h-[160px] overflow-y-auto border rounded-xl p-4 bg-muted/20 text-xs space-y-1.5 shadow-inner">
                      {result.errors.map((e, i) => (
                        <p key={i} className="text-muted-foreground py-1.5 border-b last:border-0 border-border/40 leading-relaxed">{e}</p>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : null}
          </div>
        )}
      </div>

      {/* Sticky footer for actions */}
      <div className="sticky bottom-0 z-20 flex justify-between gap-3 border-t bg-background/95 p-4 mt-2 shadow-[0_-12px_20px_-16px_rgba(0,0,0,0.1)] backdrop-blur">
        {step === 1 && (
           <>
              <Button variant="outline" onClick={onClose} className="min-w-[100px]">Cancel</Button>
              <div />
           </>
        )}
        {step === 2 && (
           <>
              <Button variant="outline" onClick={() => setStep(1)} className="min-w-[100px]">Back</Button>
              <Button onClick={handleImport} disabled={!canImport} className="gap-2 min-w-[160px] shadow-sm">
                <Upload className="w-4 h-4" /> {mode === "update" ? "Update" : "Import"} {preview.length} Products
              </Button>
           </>
        )}
        {step === 3 && !importing && (
           <>
              <Button variant="outline" onClick={() => { setStep(1); setRawRows([]); setFileName(""); setCurrentJobId(null); }}>Import Another File</Button>
              <Button onClick={onClose} className="min-w-[100px] shadow-sm">Done</Button>
           </>
        )}
      </div>
    </div>
  );
}


export default function Inventory() {
  const queryClient = useQueryClient();
  const { store } = useAuth();
  const { activeBranchId, activeBranch } = useBranch();
  const { toast } = useToast();
  const [search, setSearch] = useState("");
  const [lowStock, setLowStock] = useState(false);
  const [costOverPrice, setCostOverPrice] = useState(false);
  const [brandFilter, setBrandFilter] = useState<string>("all");
  const [categoryFilter, setCategoryFilter] = useState<string>("all");
  const [categoryFilterOpen, setCategoryFilterOpen] = useState(false);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState<any>(null);
  useEffect(() => {
    document.body.classList.toggle("product-editor-open", isCreateOpen || Boolean(editingProduct));
    return () => document.body.classList.remove("product-editor-open");
  }, [isCreateOpen, editingProduct]);
  const [uploadingImageFor, setUploadingImageFor] = useState<number | null>(null);
  const [isImportOpen, setIsImportOpen] = useState(false);
  const [importStartMethod, setImportStartMethod] = useState<ProductEntryMethod>("file");
  const [latestImportJob, setLatestImportJob] = useState<ProductImportJob | null>(null);
  const [dismissedImportJobId, setDismissedImportJobId] = useState<number | null>(null);
  const importJobStoreId = useRef<number | null>(null);
  const invalidatedImportJobId = useRef<number | null>(null);
  const [isPrintOpen, setIsPrintOpen] = useState(false);
  const [printProducts, setPrintProducts] = useState<any[] | null>(null);
  const [adjustingProduct, setAdjustingProduct] = useState<AdjustProduct | null>(null);
  const [pricingProduct, setPricingProduct] = useState<PriceAdjustProduct | null>(null);
  const [bulkPriceOpen, setBulkPriceOpen] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [resettingReorder, setResettingReorder] = useState(false);
  const [removingDuplicates, setRemovingDuplicates] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  const openProductEntry = (method: ProductEntryMethod) => {
    setImportStartMethod(method);
    setIsImportOpen(true);
  };

  useEffect(() => {
    if (new URLSearchParams(window.location.search).get("add") === "1") setIsCreateOpen(true);
  }, []);

  useEffect(() => {
    const storeId = store?.id ?? null;
    if (importJobStoreId.current !== storeId) {
      importJobStoreId.current = storeId;
      invalidatedImportJobId.current = null;
      setLatestImportJob(null);
      setDismissedImportJobId(null);
    }
    if (storeId == null) return;
    let disposed = false;
    let timeout: number | undefined;
    let retryDelay = 2500;
    const pollLatestImport = async () => {
      try {
        const response = await fetch(`${BASE}/api/products/import-jobs/latest`, { credentials: "include" });
        if (!response.ok) throw new Error("Could not load import status");
        const data = await response.json() as { job: ProductImportJob | null };
        if (disposed) return;
        retryDelay = 2500;
        const job = data.job ?? null;
        setLatestImportJob(job);
        if (job?.status === "queued" || job?.status === "processing") {
          timeout = window.setTimeout(pollLatestImport, 1200);
        }
      } catch {
        if (!disposed) {
          timeout = window.setTimeout(pollLatestImport, retryDelay);
          retryDelay = Math.min(30_000, retryDelay * 2);
        }
      }
    };
    void pollLatestImport();
    return () => {
      disposed = true;
      if (timeout !== undefined) window.clearTimeout(timeout);
    };
  }, [store?.id, latestImportJob?.id, latestImportJob?.status]);

  useEffect(() => {
    if (latestImportJob?.status !== "completed" || invalidatedImportJobId.current === latestImportJob.id) return;
    invalidatedImportJobId.current = latestImportJob.id;
    void queryClient.invalidateQueries({ queryKey: getListProductsQueryKey() });
  }, [latestImportJob?.id, latestImportJob?.status, queryClient]);

  useEffect(() => {
    setPage(1);
    setSelectedIds(new Set());
  }, [search, lowStock, costOverPrice, brandFilter, categoryFilter, activeBranchId]);

  const { data: products, isLoading } = useListProducts({
    search: search || undefined,
    lowStock: lowStock || undefined,
    branchId: activeBranchId ?? undefined,
  });
  const { data: categories, isLoading: categoriesLoading } = useListCategories();
  const createProduct = useCreateProduct();
  const updateProduct = useUpdateProduct();
  const deleteProduct = useDeleteProduct();

  const handleCreate = (data: ProductFormValues, addAnother: boolean, printAfterSave = false) => {
    const payload = {
      ...data,
      categoryId: data.categoryId && data.categoryId !== "none" ? Number(data.categoryId) : undefined,
      unit: data.unit || "piece",
      branchId: activeBranchId ?? undefined,
    } as any;
    createProduct.mutate({ data: payload }, {
      onSuccess: (created) => {
        queryClient.invalidateQueries({ queryKey: getListProductsQueryKey() });
        trackEvent("product_created", { source: "inventory_form" });
        if (printAfterSave) {
          setPrintProducts([created]);
          setIsCreateOpen(false);
          setIsPrintOpen(true);
        } else if (!addAnother) {
          setIsCreateOpen(false);
        }
      },
      onError: (error) => {
        if (isPlanUpgradeError(error)) {
          toast({
            title: "Product limit reached",
            description: productSaveError(error),
            action: <ToastAction altText="View plans" onClick={() => { window.location.href = `${BASE}/subscribe`; }}>View plans</ToastAction>,
          });
          return;
        }
        toast({ title: "Failed to save product", description: productSaveError(error), variant: "destructive" });
      },
    });
  };

  const handleUpdate = (data: ProductFormValues, _addAnother: boolean) => {
    if (!editingProduct) return;
    const payload = {
      ...data,
      categoryId: data.categoryId && data.categoryId !== "none" ? Number(data.categoryId) : undefined,
      unit: data.unit || "piece",
    } as any;
    updateProduct.mutate({ id: editingProduct.id, data: payload }, {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: getListProductsQueryKey() });
        setEditingProduct(null);
      },
      onError: (error) => toast({ title: "Failed to update product", description: productSaveError(error), variant: "destructive" }),
    });
  };

  const handleImageUpload = async (productId: number, file: File) => {
    setUploadingImageFor(productId);
    try {
      const urlRes = await fetch(`${BASE}/api/storage/uploads/product-image/request-url`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ name: file.name, size: file.size, contentType: file.type }),
      });
      const uploadDetails = await urlRes.json().catch(() => ({})) as {
        error?: string;
        uploadURL?: string;
        objectPath?: string;
      };
      if (!urlRes.ok) {
        throw new Error(uploadDetails.error ?? "Could not prepare the image upload.");
      }
      if (!uploadDetails.uploadURL || !uploadDetails.objectPath) {
        throw new Error("The upload service returned an incomplete response.");
      }

      const uploadResponse = await fetch(uploadDetails.uploadURL, {
        method: "PUT",
        headers: { "Content-Type": file.type },
        body: file,
      });
      if (!uploadResponse.ok) throw new Error("The image could not be uploaded. Please try again.");

      const temporaryUrl = `${BASE}/api/storage${uploadDetails.objectPath}`;

      const patchRes = await fetch(`${BASE}/api/products/${productId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ imageUrl: temporaryUrl }),
      });
      const updatedProduct = await patchRes.json().catch(() => ({})) as {
        error?: string;
        imageUrl?: string | null;
      };
      if (!patchRes.ok) {
        throw new Error(updatedProduct.error ?? "Failed to verify and save the image.");
      }
      if (typeof updatedProduct.imageUrl !== "string") {
        throw new Error("The image was uploaded but could not be saved to the product.");
      }
      queryClient.invalidateQueries({ queryKey: getListProductsQueryKey() });
      setEditingProduct((prev: any) => prev ? { ...prev, imageUrl: updatedProduct.imageUrl } : prev);
    } catch (err) {
      toast({
        title: "Image upload failed",
        description: err instanceof Error ? err.message : "Please choose another image and try again.",
        variant: "destructive",
      });
    } finally {
      setUploadingImageFor(null);
    }
  };

  const handleImageRemove = async (productId: number) => {
    try {
      const response = await fetch(`${BASE}/api/products/${productId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ imageUrl: null }),
      });
      if (!response.ok) {
        const data = await response.json().catch(() => ({})) as { error?: string };
        throw new Error(data.error ?? "The image could not be removed.");
      }
      queryClient.invalidateQueries({ queryKey: getListProductsQueryKey() });
      setEditingProduct((prev: any) => prev ? { ...prev, imageUrl: null } : prev);
    } catch (err) {
      toast({
        title: "Could not remove image",
        description: err instanceof Error ? err.message : "Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleEdit = (product: any) => {
    setEditingProduct(product);
  };

  const handleDelete = (id: number) => {
    if (confirm("Delete this product?")) {
      deleteProduct.mutate({ id }, {
        onSuccess: () => queryClient.invalidateQueries({ queryKey: getListProductsQueryKey() }),
        onError: (error) => toast({
          title: "Could not delete product",
          description: productSaveError(error),
          variant: "destructive",
        }),
      });
    }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) return;
    if (!confirm(`Delete ${selectedIds.size} selected product${selectedIds.size !== 1 ? "s" : ""}? This cannot be undone.`)) return;
    setBulkDeleting(true);
    try {
      const res = await fetch(`${BASE}/api/products/bulk`, {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ ids: Array.from(selectedIds) }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error ?? "Bulk delete failed.");
      setSelectedIds(new Set());
      queryClient.invalidateQueries({ queryKey: getListProductsQueryKey() });
      toast({ title: "Products deleted", description: `${data.deleted ?? 0} product(s) deleted.` });
    } catch (error) {
      toast({
        title: "Could not delete products",
        description: error instanceof Error ? error.message : "Please try again.",
        variant: "destructive",
      });
    } finally {
      setBulkDeleting(false);
    }
  };

  const handleRemoveDuplicates = async () => {
    if (!confirm("This will remove duplicate products without sales history. Products linked to sales will be kept. Continue?")) return;
    setRemovingDuplicates(true);
    try {
      const res = await fetch(`${BASE}/api/products/delete-duplicates`, {
        method: "POST",
        credentials: "include",
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error ?? "Failed to remove duplicates.");
      queryClient.invalidateQueries({ queryKey: getListProductsQueryKey() });
      alert(data.message || "Done.");
    } catch (error) {
      alert(error instanceof Error ? error.message : "Something went wrong. Please try again.");
    } finally {
      setRemovingDuplicates(false);
    }
  };

  const handleResetReorderLevels = async () => {
    if (!confirm("This will set the reorder level to 0 for ALL your products — warnings will only show when a product is completely out of stock. Continue?")) return;
    setResettingReorder(true);
    try {
      const res = await fetch(`${BASE}/api/products/reset-reorder-levels`, {
        method: "POST",
        credentials: "include",
      });
      if (!res.ok) throw new Error("Reset failed");
      queryClient.invalidateQueries({ queryKey: getListProductsQueryKey() });
      alert("Done! Reorder level reset to 0 for all products.");
    } catch {
      alert("Something went wrong. Please try again.");
    } finally {
      setResettingReorder(false);
    }
  };

  const toggleSelect = (id: number) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    const allPageSelected = pagedProducts.length > 0 && pagedProducts.every(p => selectedIds.has(p.id));
    if (allPageSelected) {
      setSelectedIds(prev => {
        const next = new Set(prev);
        pagedProducts.forEach(p => next.delete(p.id));
        return next;
      });
    } else {
      setSelectedIds(prev => {
        const next = new Set(prev);
        pagedProducts.forEach(p => next.add(p.id));
        return next;
      });
    }
  };

  const handleExport = () => {
    if (!products || products.length === 0) return;
    const rows = products.map(p => ({
      Name: p.name,
      SKU: p.sku,
      Brand: (p as any).brand ?? "",
      Category: p.categoryName ?? "",
      "Selling Price (₦)": p.price,
      "Cost Price (₦)": p.costPrice,
      Stock: p.stock,
      "Reorder Level": p.reorderLevel,
      Unit: p.unit ?? "",
      Description: p.description ?? "",
      "Date Added": format(new Date(p.createdAt), "dd/MM/yyyy"),
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    ws["!cols"] = [22, 14, 16, 18, 16, 8, 14, 10, 30, 14].map(w => ({ wch: w }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Products");
    const storeName = store?.name ? store.name.replace(/\s+/g, "_") : "store";
    XLSX.writeFile(wb, `${storeName}_products_${format(new Date(), "yyyy-MM-dd")}.xlsx`);
  };

  const allBrands = Array.from(new Set((products || []).map(p => (p as any).brand).filter(Boolean))) as string[];
  const allCategoryNames = Array.from(new Set([
    ...(categories ?? []).map(category => category.name),
    ...((products ?? []).map(product => product.categoryName).filter((name): name is string => Boolean(name))),
  ])).sort((a, b) => a.localeCompare(b));

  const costOverPriceCount = (products ?? []).filter(p => Number(p.costPrice) > Number(p.price) && Number(p.price) > 0).length;

  const filteredProducts = sortProductsBySearch((products ?? []).filter(p => {
    if (!matchesProductSearch(p, search)) return false;
    if (brandFilter !== "all" && (p as any).brand !== brandFilter) return false;
    if (categoryFilter !== "all" && p.categoryName !== categoryFilter) return false;
    if (costOverPrice && !(Number(p.costPrice) > Number(p.price) && Number(p.price) > 0)) return false;
    return true;
  }), search);

  const totalItems = filteredProducts.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
  const safePage = Math.min(page, totalPages);
  const pagedProducts = filteredProducts.slice((safePage - 1) * pageSize, safePage * pageSize);

  const editDefaults: ProductFormValues | undefined = editingProduct ? {
    name: editingProduct.name,
    sku: editingProduct.sku,
    description: editingProduct.description || "",
    categoryId: editingProduct.categoryId ? String(editingProduct.categoryId) : "none",
    brand: (editingProduct as any).brand || "",
    price: Number(editingProduct.price),
    costPrice: Number(editingProduct.costPrice),
    stock: editingProduct.stock,
    reorderLevel: editingProduct.reorderLevel,
    unit: editingProduct.unit || "piece",
    isService: (editingProduct as any).isService ?? false,
    vatApplicable: (editingProduct as any).vatApplicable ?? false,
    hasVariants: (editingProduct as any).hasVariants ?? false,
    variants: (editingProduct as any).variants ?? "[]",
  } as ProductFormValues : undefined;

  const planLimit = store?.plan === "enterprise" ? null : store?.plan === "pro" ? 2000 : store?.plan === "basic" ? 1500 : null;
  const productCount = products?.length ?? 0;
  const stockWarningCount = (products ?? []).filter(p =>
    !(p as any).isService && Number(p.stock) <= Number(p.reorderLevel)
  ).length;
  const duplicateCount = Object.values(
    (products ?? []).reduce<Record<string, number>>((counts, product) => {
      const key = product.name.trim().toLowerCase();
      if (key) counts[key] = (counts[key] ?? 0) + 1;
      return counts;
    }, {})
  ).reduce((total, count) => total + Math.max(0, count - 1), 0);
  const planName = store?.plan
    ? `${store.plan.charAt(0).toUpperCase()}${store.plan.slice(1)} Plan`
    : "Trial Plan";
  const labelSources = printProducts ?? products ?? [];
  const unprintableNames = labelSources
    .filter(product => product.hasVariants && !parseLabelVariants(product.variants)?.length)
    .map(product => product.name);
  const labelPrinter = store?.id != null && (
    <BarcodePrinter
      open={isPrintOpen}
      onClose={() => { setIsPrintOpen(false); setPrintProducts(null); }}
      products={printableProducts(labelSources)}
      unprintableNames={unprintableNames}
      storeId={store.id}
      storeName={store.name ?? ""}
      autoSelectAll={printProducts !== null}
    />
  );

  if (new URLSearchParams(window.location.search).get("view") === "variants") {
    return <>
      <VariantMatrix products={(products ?? []) as any[]} isLoading={isLoading}
        onPrintVariant={(product, variant) => {
          setPrintProducts([printableVariant(product, variant)]);
          setIsPrintOpen(true);
        }}
      />
      {labelPrinter}
    </>;
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div className="min-w-0">
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="text-3xl font-bold tracking-tight">Inventory</h1>
            <Badge variant="secondary" className="whitespace-nowrap rounded-full px-2.5 py-1 text-xs font-semibold">
              {planName}
            </Badge>
          </div>
          <div className="flex items-center gap-2 flex-wrap text-sm text-muted-foreground mt-2">
            <span className="font-medium text-foreground">
              {planLimit ? `${productCount.toLocaleString()} / ${planLimit.toLocaleString()}` : productCount.toLocaleString()} product{productCount === 1 ? "" : "s"}
            </span>
            <span aria-hidden="true">•</span>
            <span>{activeBranch ? `Showing stock at ${activeBranch.name}` : "Showing stock across all locations"}</span>
          </div>
        </div>

        <div className="flex items-center gap-2 flex-wrap justify-end shrink-0">
          <Button
            variant="outline"
            className="md:hidden gap-2 rounded-lg"
            disabled={!products?.length}
            onClick={() => { setPrintProducts(null); setIsPrintOpen(true); }}
          >
            <Printer className="w-4 h-4" /> Print Barcodes
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="gap-2 rounded-lg">
                <SlidersHorizontal className="w-4 h-4" />
                Tools
                <ChevronDown className="w-4 h-4 opacity-60" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>Inventory tools</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onSelect={handleExport} disabled={!products || products.length === 0}>
                <Download /> Export Excel
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={() => openProductEntry("file")}>
                <FileSpreadsheet /> Import CSV/Excel
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={() => setBulkPriceOpen(true)} disabled={!products?.length}>
                <TrendingUp /> Bulk Price Update
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={() => { setPrintProducts(null); setIsPrintOpen(true); }} disabled={!products || products.length === 0}>
                <Printer /> Print Labels
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          <Button
            variant="outline"
            size="sm"
            onClick={handleResetReorderLevels}
            disabled={resettingReorder || !products || products.length === 0}
            className="gap-1.5 rounded-full text-amber-700 border-amber-300 hover:bg-amber-50 dark:text-amber-300 dark:border-amber-700 dark:hover:bg-amber-950/40"
          >
            {resettingReorder ? <Loader2 className="w-4 h-4 animate-spin" /> : <AlertTriangle className="w-4 h-4" />}
            <span>Fix Warnings</span>
            <Badge variant="secondary" className="min-w-5 h-5 justify-center rounded-full px-1 text-[11px]">{stockWarningCount}</Badge>
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleRemoveDuplicates}
            disabled={removingDuplicates || !products || products.length === 0}
            className="gap-1.5 rounded-full text-rose-700 border-rose-300 hover:bg-rose-50 dark:text-rose-300 dark:border-rose-700 dark:hover:bg-rose-950/40"
          >
            {removingDuplicates ? <Loader2 className="w-4 h-4 animate-spin" /> : <Layers className="w-4 h-4" />}
            <span>Duplicates</span>
            <Badge variant="secondary" className="min-w-5 h-5 justify-center rounded-full px-1 text-[11px]">{duplicateCount}</Badge>
          </Button>
          <Button
            variant="outline"
            onClick={() => openProductEntry("grid")}
            data-testid="button-add-multiple-products"
            className="gap-2 rounded-lg"
            title="Enter or paste several separate products in a spreadsheet-style grid"
          >
            <FileSpreadsheet className="w-4 h-4" /> Bulk Add Products
          </Button>
          <Button variant="outline" asChild className="gap-2 rounded-lg">
            <Link href="/inventory/matrix-entry" title="Create one style with stock tracked by two variant attributes">
              <Package className="w-4 h-4" /> Add Variant Matrix
            </Link>
          </Button>
          <Button
            onClick={() => setIsCreateOpen(true)}
            data-testid="button-add-product"
            className="gap-2 rounded-lg bg-[#3155D9] text-white border-[#3155D9] shadow-sm hover:bg-[#2847B5]"
          >
            <Plus className="w-4 h-4" /> Add Product
          </Button>
        </div>
      </div>

      {latestImportJob && latestImportJob.id !== dismissedImportJobId && !isImportOpen && (
        <ProductImportSummary
          job={latestImportJob}
          onDismiss={() => setDismissedImportJobId(latestImportJob.id)}
        />
      )}

      {/* Add Product — full side panel */}
      <Sheet open={isCreateOpen} onOpenChange={setIsCreateOpen}>
        <SheetContent side="right" className="w-full sm:max-w-2xl p-0 flex flex-col">
          <SheetHeader className="px-6 pt-5 pb-0 flex-shrink-0">
            <SheetTitle className="text-xl">Add New Product</SheetTitle>
          </SheetHeader>
          <div className="flex-1 flex flex-col min-h-0">
            <ProductForm
              categories={categories}
              categoriesLoading={categoriesLoading}
              brands={allBrands}
              onSubmit={handleCreate}
              onCancel={() => setIsCreateOpen(false)}
              isPending={createProduct.isPending}
            />
          </div>
        </SheetContent>
      </Sheet>

      {/* Edit Product — full side panel */}
      <Sheet open={!!editingProduct} onOpenChange={(o) => !o && setEditingProduct(null)}>
        <SheetContent side="right" className="w-full sm:max-w-2xl p-0 flex flex-col">
          <SheetHeader className="px-6 pt-5 pb-0 flex-shrink-0">
            <SheetTitle className="text-xl">Edit Product</SheetTitle>
          </SheetHeader>
          {/* Product Image Upload */}
          {editingProduct && (
            <div className="px-6 pt-4 pb-0 flex-shrink-0">
              <div className="flex items-center gap-4 p-3 border rounded-xl bg-muted/30">
                <div className="w-16 h-16 rounded-lg border bg-white flex items-center justify-center overflow-hidden shrink-0">
                  {editingProduct.imageUrl
                    ? <img src={editingProduct.imageUrl} alt="Product" className="w-full h-full object-cover" />
                    : <Package className="w-7 h-7 text-muted-foreground/40" />}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium mb-1">Product Image</p>
                  <p className="text-xs text-muted-foreground mb-2">JPG, PNG, or WebP up to 5 MB. A clean white background works well in your storefront.</p>
                  <div className="flex gap-2">
                    <label className="cursor-pointer">
                      <input type="file" accept="image/jpeg,image/png,image/webp" className="sr-only" onChange={e => {
                        const f = e.target.files?.[0];
                        if (f) handleImageUpload(editingProduct.id, f);
                        e.target.value = "";
                      }} />
                      <span className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 border rounded-md bg-white hover:bg-muted cursor-pointer">
                        {uploadingImageFor === editingProduct.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />}
                        {uploadingImageFor === editingProduct.id ? "Uploading..." : "Upload Image"}
                      </span>
                    </label>
                    {editingProduct.imageUrl && (
                      <button type="button" onClick={() => handleImageRemove(editingProduct.id)} className="text-xs px-3 py-1.5 border rounded-md text-destructive hover:bg-destructive/5">Remove</button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
          <div className="flex-1 flex flex-col min-h-0">
            <ProductForm
              key={editingProduct?.id}
              defaultValues={editDefaults}
              categories={categories}
              categoriesLoading={categoriesLoading}
              brands={allBrands}
              productId={editingProduct?.id}
              onSubmit={handleUpdate}
              onCancel={() => setEditingProduct(null)}
              onPrintBarcode={product => {
                setEditingProduct(null);
                setPrintProducts([editingProduct]);
                window.setTimeout(() => setIsPrintOpen(true), 200);
              }}
              isPending={updateProduct.isPending}
              isEdit
            />
          </div>
        </SheetContent>
      </Sheet>

      <Dialog open={isImportOpen} onOpenChange={setIsImportOpen}>
        <DialogContent className="max-h-[90vh] w-[95vw] max-w-6xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileSpreadsheet className="w-5 h-5" /> Add Products in Bulk
            </DialogTitle>
          </DialogHeader>
          <ImportModal
            initialEntryMethod={importStartMethod}
            onClose={() => setIsImportOpen(false)}
            importJob={latestImportJob}
            onJobUpdate={job => {
              setLatestImportJob(job);
              setDismissedImportJobId(null);
            }}
          />
        </DialogContent>
      </Dialog>

      <Dialog open={!!adjustingProduct} onOpenChange={open => { if (!open) setAdjustingProduct(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <SlidersHorizontal className="w-5 h-5" /> Adjust Stock
            </DialogTitle>
          </DialogHeader>
          {adjustingProduct && (
            <StockAdjustDialog
              product={adjustingProduct}
              onClose={() => setAdjustingProduct(null)}
            />
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!pricingProduct} onOpenChange={open => { if (!open) setPricingProduct(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <TrendingUp className="w-5 h-5" /> Adjust Price
            </DialogTitle>
          </DialogHeader>
          {pricingProduct && (
            <PriceAdjustDialog
              product={pricingProduct}
              onClose={() => setPricingProduct(null)}
            />
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={bulkPriceOpen} onOpenChange={setBulkPriceOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <TrendingUp className="w-5 h-5" /> Bulk Price Update
            </DialogTitle>
          </DialogHeader>
          <BulkPriceAdjustDialog
            productIds={selectedIds.size > 0 ? Array.from(selectedIds) : filteredProducts.map(product => product.id)}
            productCount={selectedIds.size > 0 ? selectedIds.size : filteredProducts.length}
            onClose={() => setBulkPriceOpen(false)}
            onSaved={updated => {
              queryClient.invalidateQueries({ queryKey: getListProductsQueryKey() });
              setBulkPriceOpen(false);
              setSelectedIds(new Set());
              toast({ title: "Prices updated", description: `${updated} product${updated === 1 ? "" : "s"} updated successfully.` });
            }}
          />
        </DialogContent>
      </Dialog>

      {labelPrinter}

      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input placeholder="Search products..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9" />
        </div>
        {allBrands.length > 0 && (
          <Select value={brandFilter} onValueChange={v => setBrandFilter(v)}>
            <SelectTrigger className="w-[150px]">
              <SelectValue placeholder="All brands" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All brands</SelectItem>
              {allBrands.map(b => <SelectItem key={b} value={b}>{b}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
        {allCategoryNames.length > 0 && (
          <Popover open={categoryFilterOpen} onOpenChange={setCategoryFilterOpen}>
            <PopoverTrigger asChild>
              <Button type="button" variant="outline" role="combobox" aria-expanded={categoryFilterOpen} data-testid="button-filter-product-category" className="w-[160px] justify-between">
                {categoryFilter === "all" ? "All categories" : categoryFilter}
                <ChevronsUpDown className="h-4 w-4 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent align="start" className="w-[--radix-popover-trigger-width] p-0">
              <Command>
                <CommandInput placeholder="Search categories…" data-testid="input-search-inventory-category" />
                <CommandList>
                  <CommandEmpty>No matching category.</CommandEmpty>
                  <CommandGroup>
                    {[{ value: "all", label: "All categories" }, ...allCategoryNames.map(name => ({ value: name, label: name }))].map(option => (
                      <CommandItem key={option.value} value={option.label} onSelect={() => { setCategoryFilter(option.value); setCategoryFilterOpen(false); }}>
                        <Check className={`mr-2 h-4 w-4 ${categoryFilter === option.value ? "opacity-100" : "opacity-0"}`} />{option.label}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        )}
        {(brandFilter !== "all" || categoryFilter !== "all") && (
          <Button variant="ghost" size="sm" className="text-muted-foreground gap-1.5" onClick={() => { setBrandFilter("all"); setCategoryFilter("all"); }}>
            <X className="w-3.5 h-3.5" /> Clear filters
          </Button>
        )}
        <Button variant={lowStock ? "default" : "outline"} onClick={() => setLowStock(!lowStock)}>
          <AlertTriangle className="w-4 h-4 mr-2" /> Low Stock
        </Button>
        {costOverPriceCount > 0 && (
          <Button variant={costOverPrice ? "destructive" : "outline"} onClick={() => setCostOverPrice(!costOverPrice)} className={costOverPrice ? "" : "border-orange-300 text-orange-600 hover:bg-orange-50"}>
            <TrendingDown className="w-4 h-4 mr-2" /> Cost &gt; Price {!costOverPrice && <span className="ml-1 bg-orange-100 text-orange-700 text-xs font-semibold px-1.5 py-0.5 rounded-full">{costOverPriceCount}</span>}
          </Button>
        )}
      </div>

      {selectedIds.size > 0 && (
        <div className="flex items-center justify-between px-4 py-2.5 bg-destructive/10 border border-destructive/20 rounded-md">
          <span className="text-sm font-medium text-destructive">{selectedIds.size} product{selectedIds.size !== 1 ? "s" : ""} selected</span>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => setSelectedIds(new Set())}>Deselect All</Button>
            <Button variant="outline" size="sm" className="gap-2" onClick={() => setBulkPriceOpen(true)}>
              <TrendingUp className="w-3.5 h-3.5" /> Adjust Prices
            </Button>
            <Button variant="destructive" size="sm" className="gap-2" onClick={handleBulkDelete} disabled={bulkDeleting}>
              {bulkDeleting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
              Delete {selectedIds.size} Product{selectedIds.size !== 1 ? "s" : ""}
            </Button>
          </div>
        </div>
      )}

      <div className="border rounded-md overflow-hidden">
        <Table>
          <TableHeader className="sticky top-0 bg-background z-10">
            <TableRow>
              <TableHead className="w-10">
                <Checkbox
                  checked={pagedProducts.length > 0 && pagedProducts.every(p => selectedIds.has(p.id))}
                  onCheckedChange={toggleSelectAll}
                />
              </TableHead>
              <TableHead>Product</TableHead>
              <TableHead>SKU</TableHead>
              <TableHead>Category</TableHead>
              <TableHead>Brand</TableHead>
              <TableHead>Price</TableHead>
              <TableHead>Cost</TableHead>
              <TableHead>Stock</TableHead>
              <TableHead>Unit</TableHead>
              <TableHead className="w-[100px]">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow><TableCell colSpan={10} className="text-center py-8"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></TableCell></TableRow>
            ) : filteredProducts.length === 0 ? (
              <TableRow>
                <TableCell colSpan={10} className="py-16">
                  <div className="flex flex-col items-center gap-3 text-center">
                    <div className="w-12 h-12 rounded-xl bg-muted flex items-center justify-center">
                      <Package className="w-6 h-6 text-muted-foreground/50" />
                    </div>
                    <div>
                      <p className="font-semibold">No products yet</p>
                      <p className="text-sm text-muted-foreground mt-0.5">Add your first product to start tracking inventory and making sales.</p>
                    </div>
                    <Button size="sm" className="gap-2 mt-1" onClick={() => setIsCreateOpen(true)}>
                      <Plus className="w-4 h-4" /> Add Your First Product
                    </Button>
                    <p className="text-xs text-muted-foreground/60">💡 Tip: Add a category first to keep products organised</p>
                  </div>
                </TableCell>
              </TableRow>
            ) : pagedProducts.map(p => (
              <TableRow key={p.id} className={selectedIds.has(p.id) ? "bg-muted/40" : ""}>
                <TableCell><Checkbox checked={selectedIds.has(p.id)} onCheckedChange={() => toggleSelect(p.id)} /></TableCell>
                <TableCell className="font-medium">{p.name}</TableCell>
                <TableCell className="text-muted-foreground">{p.sku}</TableCell>
                <TableCell>{p.categoryName ? <Badge variant="secondary">{p.categoryName}</Badge> : <span className="text-muted-foreground">—</span>}</TableCell>
                <TableCell>{(p as any).brand ? <Badge variant="outline">{(p as any).brand}</Badge> : <span className="text-muted-foreground">—</span>}</TableCell>
                <TableCell>₦{Number(p.price).toFixed(2)}</TableCell>
                <TableCell>₦{Number(p.costPrice).toFixed(2)}</TableCell>
                <TableCell>
                  {(p as any).isService ? (
                    <Badge variant="outline" className="text-violet-700 border-violet-300 bg-violet-50">Service</Badge>
                  ) : (
                    <div className="space-y-0.5">
                      <span className={p.stock <= p.reorderLevel ? "text-destructive font-semibold" : ""}>
                        {p.stock}
                        {p.stock <= p.reorderLevel && <AlertTriangle className="inline w-3 h-3 ml-1 text-destructive" />}
                      </span>
                      {(() => {
                        const ed = (p as any).expiryDate as string | null;
                        if (!ed) return null;
                        const today = new Date(); today.setHours(0,0,0,0);
                        const exp = new Date(ed); exp.setHours(0,0,0,0);
                        const daysLeft = Math.ceil((exp.getTime() - today.getTime()) / 86400000);
                        if (daysLeft < 0) return <Badge variant="destructive" className="text-[10px] px-1 py-0">Expired</Badge>;
                        if (daysLeft <= 30) return <Badge variant="outline" className="text-[10px] px-1 py-0 text-orange-700 border-orange-300 bg-orange-50">Exp {daysLeft}d</Badge>;
                        return null;
                      })()}
                    </div>
                  )}
                </TableCell>
                <TableCell className="text-muted-foreground">{p.unit}</TableCell>
                <TableCell>
                  <div className="flex items-center gap-1">
                    {!(p as any).isService && <Button
                      variant="ghost" size="icon"
                      title="Adjust Stock"
                      onClick={() => setAdjustingProduct({
                        id: p.id,
                        name: p.name,
                        sku: p.sku,
                        stock: p.stock,
                        unit: p.unit ?? "piece",
                        hasVariants: Boolean((p as any).hasVariants),
                        variants: (p as any).variants ?? null,
                      })}
                    >
                      <SlidersHorizontal className="w-4 h-4" />
                    </Button>}
                    <Button
                      variant="ghost" size="icon"
                      title="Adjust Price"
                      onClick={() => setPricingProduct({ id: p.id, name: p.name, sku: p.sku, price: Number(p.price), costPrice: Number(p.costPrice) })}
                    >
                      <TrendingUp className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      title="Print Barcode"
                      aria-label={`Print barcode for ${p.name}`}
                      onClick={() => {
                        setPrintProducts([p]);
                        setIsPrintOpen(true);
                      }}
                    >
                      <Printer className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => handleEdit(p)}><Edit className="w-4 h-4" /></Button>
                    <Button variant="ghost" size="icon" className="text-destructive hover:text-destructive" onClick={() => handleDelete(p.id)}><Trash2 className="w-4 h-4" /></Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {totalItems > 0 && (
        <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
          <div className="flex items-center gap-1">
            <Button
              variant="outline" size="sm"
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={safePage <= 1}
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            {getPageNumbers(safePage, totalPages).map((pg, i) =>
              pg === "..." ? (
                <span key={`e-${i}`} className="px-2 text-sm text-muted-foreground select-none">…</span>
              ) : (
                <Button
                  key={pg}
                  variant={pg === safePage ? "default" : "outline"}
                  size="sm"
                  className="w-9"
                  onClick={() => setPage(pg as number)}
                >
                  {pg}
                </Button>
              )
            )}
            <Button
              variant="outline" size="sm"
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={safePage >= totalPages}
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm text-muted-foreground">
              {(safePage - 1) * pageSize + 1}–{Math.min(safePage * pageSize, totalItems)} of {totalItems}
            </span>
            <Select value={String(pageSize)} onValueChange={v => { setPageSize(Number(v)); setPage(1); }}>
              <SelectTrigger className="w-28 h-8 text-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {[10, 20, 50, 100, 250, 500].map(n => (
                  <SelectItem key={n} value={String(n)}>{n} / page</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      )}

      {(products?.length ?? 0) > 0 && (() => {
        const totalProducts = products!.length;
        const totalStockValue = products!.reduce((s, p) => s + Number(p.price) * p.stock, 0);
        const totalCostValue = products!.reduce((s, p) => s + Number(p.costPrice) * p.stock, 0);
        return (
          <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-2.5 bg-muted/30 border rounded-md text-sm">
            <span className="text-muted-foreground font-medium">{totalProducts} product{totalProducts !== 1 ? "s" : ""}</span>
            <div className="flex gap-6">
              <span className="text-muted-foreground">Stock Value (cost): <strong className="text-foreground">{formatCurrency(totalCostValue)}</strong></span>
              <span className="text-muted-foreground">Retail Value: <strong className="text-foreground">{formatCurrency(totalStockValue)}</strong></span>
              {totalCostValue > totalStockValue && costOverPriceCount > 0 && (
                <button onClick={() => setCostOverPrice(true)} className="flex items-center gap-1 text-xs text-orange-600 font-medium hover:underline">
                  <AlertTriangle className="w-3.5 h-3.5" /> {costOverPriceCount} product{costOverPriceCount !== 1 ? "s" : ""} with cost &gt; price — click to review
                </button>
              )}
            </div>
          </div>
        );
      })()}
    </div>
  );
}
